import { Inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { Observable, of } from 'rxjs'
import { AuthContextStorageService } from '../../../../core/auth/auth-context-storage.service'
import { BarterControllerService, ListResponseBarterListItemDto } from '../../../../api/backend'
import { SelectedDepartmentService } from '../../../../core/services/selected-department.service'
import { catchError, filter, map, switchMap, take } from 'rxjs/operators'
import { PerfectScrollbarActions } from '../../../../shared/ui/perfect-scrollbar/state/actions-type'
import { StoreKeysEnum } from '../../../../store/store-keys'
import { BarterListActions } from './action-types'
import { IBarterListFilterCriteria } from './barter-list.reducer'
import { selectBarterListFilterCriteria, selectBarterListState } from './barter-list.selectors'
import { FilterCriteriaStorageService } from '../../../../core/storage/filter-criteria-storage.service'
import DEPARTMENT_COUNT_FILTER_TYPE from '../../../../constants/departmentCountFilterType.constant'
import { SubjectSearchActions } from '../../../../domain/subject/components/subject-search/state/action-types'
import { BarterSearchActions } from '../../components/barter-search/state/action-types'
import { BarterStateActions } from '../../components/barter-state/state/action-types'
import { SortByActions } from '../../../../shared/ui/sort-by/state/action-types'
import cloneDeep from 'lodash/cloneDeep'

@Injectable()
export class BarterListEffects {

    fetchData$: Observable<any>
    filterCriteriaAction$: Observable<any>
    itemCollectionAction$: Observable<any>

    filter$: Observable<any>
    setStoredOrDefaultCriteria$: Observable<any>
    sort$: Observable<any>
    updateFilterCriteriaAndFilter$: Observable<any>

    private filterCriteriaStorageKey = '-bartersFilterCriteria'

    constructor(@Inject(Actions) private actions$: Actions,
                @Inject(Store) private store: Store,
                @Inject(AuthContextStorageService) private authContextStorageService: AuthContextStorageService,
                @Inject(BarterControllerService) private barterControllerService: BarterControllerService,
                @Inject(FilterCriteriaStorageService) private filterCriteriaStorageService: FilterCriteriaStorageService,
                @Inject(SelectedDepartmentService) private selectedDepartmentService: SelectedDepartmentService) {
        // =======================
        // Actions called from PerfectScrollbar
        // reflects dataLoaderAction from origin component
        // =======================
        this.fetchData$ = createEffect(() => this.actions$.pipe(
            ofType(PerfectScrollbarActions.fetchOriginData),
            filter((action) => action.key === StoreKeysEnum.BarterListFeature),
            switchMap((action) =>
                this.fetchData().pipe(
                    map(data => PerfectScrollbarActions.postLoadAction({ key: action.key, data })),
                    catchError(() => of(BarterListActions.noAction())),
                ),
            ),
        ))
        // reflects filterCriteriaAction from origin component
        this.filterCriteriaAction$ = createEffect(() => this.actions$.pipe(
            ofType(PerfectScrollbarActions.originFilterCriteriaAction),
            filter((action) => action.key === StoreKeysEnum.BarterListFeature),
            map((action) =>
                BarterListActions.updateFilterCriteria({ criteriaFragment: action.fragment }),
            ),
        ))
        // reflects itemCollectionAction from origin component
        this.itemCollectionAction$ = createEffect(() => this.actions$.pipe(
            ofType(PerfectScrollbarActions.setOriginItemCollection),
            filter((action) => action.key === StoreKeysEnum.BarterListFeature),
            map((action) =>
                BarterListActions.setItemCollection({ itemCollection: action.collection }),
            ),
        ))

        this.filter$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterListActions.filter),
                switchMap(() => {
                    return this.store.select(selectBarterListFilterCriteria).pipe(
                        take(1),
                        switchMap(filterCriteria => {
                            let criteria = { ...filterCriteria }

                            this.filterCriteriaStorageService.storeFilterCriteria(this.filterCriteriaStorageKey, criteria)

                            // force data reload
                            criteria.offset = 0

                            return [
                                BarterListActions.setFilterCriteria({ filterCriteria: criteria }),
                                PerfectScrollbarActions.filterChanged({ key: StoreKeysEnum.BarterListFeature }),
                            ]
                        }),
                    )
                }),
            )
        })

        this.setStoredOrDefaultCriteria$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(BarterListActions.setStoredOrDefaultCriteria),
                switchMap(() => {
                    return this.store.select(selectBarterListState).pipe(
                        take(1),
                        switchMap(state => {
                            const filter = state.filter

                            const actions = []

                            if (filter) {
                                actions.push(BarterListActions.resetFilterCriteria())
                            }

                            const defaultCriteria = cloneDeep(state.filterCriteria)
                            const storageCriteria = this.filterCriteriaStorageService.readFilterCriteria(this.filterCriteriaStorageKey)
                            let filterCriteria = this.filterCriteriaStorageService.getStoredOrDefaultCriteria(storageCriteria, defaultCriteria)

                            if (filter) {

                                // při filtru z dlaždice z LP ignorujeme dříve nastavené filtry
                                filterCriteria = cloneDeep(defaultCriteria)
                                this.store.dispatch(PerfectScrollbarActions.resetContractCache({key: StoreKeysEnum.BarterListFeature}))

                                switch (filter) {
                                    case 'department-none':
                                        filterCriteria.departmentCountFilterType = DEPARTMENT_COUNT_FILTER_TYPE.typeNone
                                        filterCriteria.state = []
                                        break
                                    case 'department-more':
                                        filterCriteria.departmentCountFilterType = DEPARTMENT_COUNT_FILTER_TYPE.typeMore
                                        filterCriteria.state = []
                                        break
                                }
                            }

                            actions.push(SubjectSearchActions.updateFilterCriteria({ criteriaFragment: { searchText: filterCriteria.counterpartySearchText } }))
                            actions.push(BarterSearchActions.updateFilterCriteria({ criteriaFragment: { searchText: filterCriteria.barterNumberOfficial } }))
                            actions.push(BarterStateActions.setStates({ states: filterCriteria.state }))
                            actions.push(BarterListActions.resetFilter())
                            actions.push(BarterListActions.setFilterCriteria({ filterCriteria: filterCriteria }))
                            return actions
                        }),
                    )
                }),
            )
        })

        this.sort$ = createEffect(() => {
            return this.actions$.pipe(
                ofType(SortByActions.sort),
                filter((action) => action.key === StoreKeysEnum.BarterListFeature),
                switchMap((action) => {
                    return this.store.select(selectBarterListFilterCriteria).pipe(
                        take(1),
                        switchMap((filterCriteria) => {
                            let sortedBy = filterCriteria.sortedBy
                            let sortDir = filterCriteria.sortDir

                            if (sortedBy === action.sortValue) {
                                sortDir = sortDir === 'asc' ? 'desc' : 'asc'
                            } else {
                                sortedBy = action.sortValue
                                sortDir = 'asc'
                            }
                            return [
                                BarterListActions.updateFilterCriteriaAndFilter({
                                    criteriaFragment: {
                                        sortedBy: sortedBy,
                                        sortDir: sortDir,
                                        offset: 0,
                                    },
                                }),
                            ]
                        }),
                    )
                }),
            )
        })

        this.updateFilterCriteriaAndFilter$ = createEffect(() => this.actions$.pipe(
            ofType(BarterListActions.updateFilterCriteriaAndFilter),
            map(() => BarterListActions.filter()),
        ))
    }

    private fetchData(): Observable<ListResponseBarterListItemDto> {
        return this.store.select(selectBarterListFilterCriteria).pipe(
            take(1),
            switchMap((filterCriteria: IBarterListFilterCriteria) => {
                return this.barterControllerService.getBartersByFilter(
                    this.authContextStorageService.getProjectKey(),
                    this.selectedDepartmentService.getSelectedDepartmentId(),
                    filterCriteria.departmentCountFilterType ?? null,
                    filterCriteria.offset,
                    filterCriteria.limit,
                    filterCriteria.sortDir,
                    filterCriteria.sortedBy,
                    filterCriteria.barterNumberOfficial,
                    filterCriteria.concernedSubjectId,
                    filterCriteria.concernedSubjectSearchText,
                    filterCriteria.counterpartyId,
                    filterCriteria.counterpartySearchText,
                    filterCriteria.state,
                )
            }),
        )
    }
}
